import { GUEST_CONTENTFUL_PAGE_SIZE } from '@/core/api/services/guestProjectsService';
import { CONTENTFUL_TAGS } from '@/core/constants/contentfulTags';
import { fetchPropertyAssetsByTag } from '@/core/services/contentfulClient';
import { useTranslation } from '@/core/hooks';
import { resolveBrochureUrlsFromAssets } from '@/core/utils/brochureDownload';
import { useQuery } from '@tanstack/react-query';

type GuestProjectImageMap = Readonly<
  Record<string, ReadonlyArray<{ readonly uri: string }>>
>;

type GuestProjectBrochureMap = Readonly<Record<string, ReadonlyArray<string>>>;
type GuestProjectTextMap = Readonly<Record<string, string>>;
type GuestProjectOrderMap = Readonly<Record<string, number>>;

export interface GuestContentfulFetchSnapshot {
  readonly tagId: string;
  readonly locale: string;
  readonly projectCount: number;
  readonly projectIds: ReadonlyArray<string>;
  /** Mapped property assets array from the guest-tag Contentful query. */
  readonly assets: ReadonlyArray<{
    readonly projectId: string;
    readonly name?: string;
    readonly description?: string;
    readonly order?: number;
    readonly brochureUrl?: string;
    readonly brochureUrls?: ReadonlyArray<string>;
    readonly imageCount: number;
  }>;
  readonly rawResponse: unknown;
}

export interface UseGuestProjectAssetsResult {
  readonly imagesByProjectId: GuestProjectImageMap;
  /** Contentful brochure URL(s) by project_id (omitted keys = no brochure). */
  readonly brochureUrlsByProjectId: GuestProjectBrochureMap;
  /** Localized Contentful `name` by project_id. */
  readonly nameByProjectId: GuestProjectTextMap;
  /** Localized Contentful `description` by project_id. */
  readonly descriptionByProjectId: GuestProjectTextMap;
  /** Contentful `order` by project_id (omitted = unordered). */
  readonly orderByProjectId: GuestProjectOrderMap;
  /** Last guest-tag Contentful fetch (for home-page logging). */
  readonly contentfulFetch: GuestContentfulFetchSnapshot | null;
  readonly isContentfulSuccess: boolean;
  /** True until the first guest-tag Contentful response settles. */
  readonly isContentfulPending: boolean;
}

function mapTaggedAssetsToGuestMaps(
  assetsByProjectId: Readonly<
    Record<
      string,
      {
        images?: ReadonlyArray<{ url?: string }>;
        brochureUrls?: ReadonlyArray<string>;
        brochureUrl?: string;
        name?: string;
        description?: string;
        order?: number;
      }
    >
  >,
): Omit<
  UseGuestProjectAssetsResult,
  'contentfulFetch' | 'isContentfulSuccess' | 'isContentfulPending'
> {
  const imagesByProjectId: Record<
    string,
    ReadonlyArray<{ readonly uri: string }>
  > = {};
  const brochureUrlsByProjectId: Record<string, ReadonlyArray<string>> = {};
  const nameByProjectId: Record<string, string> = {};
  const descriptionByProjectId: Record<string, string> = {};
  const orderByProjectId: Record<string, number> = {};

  for (const [projectId, assets] of Object.entries(assetsByProjectId)) {
    const images = (assets.images ?? [])
      .map((img) => img.url?.trim())
      .filter((url): url is string => Boolean(url))
      .map((url) => ({ uri: url }));
    imagesByProjectId[projectId] = images;

    const brochureUrls = resolveBrochureUrlsFromAssets(assets);
    if (brochureUrls.length > 0) {
      brochureUrlsByProjectId[projectId] = brochureUrls;
    }

    const name = assets.name?.trim();
    if (name) {
      nameByProjectId[projectId] = name;
    }
    const description = assets.description?.trim();
    if (description) {
      descriptionByProjectId[projectId] = description;
    }
    if (typeof assets.order === 'number' && Number.isFinite(assets.order)) {
      orderByProjectId[projectId] = assets.order;
    }
  }

  return {
    imagesByProjectId,
    brochureUrlsByProjectId,
    nameByProjectId,
    descriptionByProjectId,
    orderByProjectId,
  };
}

/**
 * Contentful images + brochure + name/description/order for guest catalogue.
 * Loads all `propertyImages` tagged {@link CONTENTFUL_TAGS.GUEST} as one array.
 * Master-plan `viewLink` is ADHA-only (not exposed on guest).
 */
export function useGuestProjectAssets(
  _projectIds?: ReadonlyArray<string>,
): UseGuestProjectAssetsResult {
  const { currentLanguage } = useTranslation();
  const locale = currentLanguage === 'ar' ? 'ar' : 'en';

  const query = useQuery({
    queryKey: ['guest', 'contentful-assets', locale, CONTENTFUL_TAGS.GUEST],
    queryFn: async (): Promise<{
      maps: Omit<
        UseGuestProjectAssetsResult,
        'contentfulFetch' | 'isContentfulSuccess' | 'isContentfulPending'
      >;
      contentfulFetch: GuestContentfulFetchSnapshot;
    }> => {
      const tagged = await fetchPropertyAssetsByTag(CONTENTFUL_TAGS.GUEST, {
        maxWidth: 960,
        locale,
        pageLimit: GUEST_CONTENTFUL_PAGE_SIZE,
      });
      const maps = mapTaggedAssetsToGuestMaps(tagged.assetsByProjectId);
      const projectIds = tagged.assets.map((item) => item.projectId);
      return {
        maps,
        contentfulFetch: {
          tagId: tagged.tagId,
          locale: tagged.locale,
          projectCount: tagged.assets.length,
          projectIds,
          assets: tagged.assets.map((item) => ({
            projectId: item.projectId,
            ...(item.name ? { name: item.name } : {}),
            ...(item.description ? { description: item.description } : {}),
            ...(item.order !== undefined ? { order: item.order } : {}),
            ...(item.brochureUrl ? { brochureUrl: item.brochureUrl } : {}),
            ...(item.brochureUrls?.length
              ? { brochureUrls: item.brochureUrls }
              : {}),
            imageCount: item.images.length,
          })),
          rawResponse: tagged.rawResponse,
        },
      };
    },
    staleTime: 5 * 60_000,
  });

  const emptyMaps = {
    imagesByProjectId: {},
    brochureUrlsByProjectId: {},
    nameByProjectId: {},
    descriptionByProjectId: {},
    orderByProjectId: {},
  };

  return {
    ...(query.data?.maps ?? emptyMaps),
    contentfulFetch: query.data?.contentfulFetch ?? null,
    isContentfulSuccess: query.isSuccess,
    isContentfulPending: query.isPending,
  };
}

/** Alias kept for guest mobile imports — prefer `useGuestProjectAssets`. */
export const useGuestProjectImages = useGuestProjectAssets;
